import { useMemo, useState } from 'react'
import { Button, Input } from '@heroui/react'
import { assignableServiceIds, moveItem, putGroup } from '../../lib/profilesDoc'
import { LIMITS } from '../../lib/profilesValidate'
import { nameIn } from '../../lib/staffText'
import type { OperationDoc, ProfileProblem, ProfileSegment, Service, ServiceGroup } from '../../lib/types'
import { Icon } from '../../ui/icons'
import { useStaffText } from '../../ui/StaffText'
import { MoveButtons } from '../recordParts'
import { Confirm } from '../sectionKit'
import { cardProblems, queueOptions, useLiveProblems, useProblemSlots } from './sheetParts'
import { InnerPage, type Crumb } from '../settingsKit'
import { Pick, Tick } from '../setupKit'

export type GroupPending = Pick<ServiceGroup, 'serviceIds' | 'defaultQueueId'>

/** The group's segments in the sheet's order; ones added while it is open come last. */
function ordered(segments: ProfileSegment[], order: string[]): ProfileSegment[] {
  const byId = new Map(segments.map((s) => [s.id, s]))
  const known = order.flatMap((id) => (byId.has(id) ? [byId.get(id)!] : []))
  return [...known, ...segments.filter((s) => !order.includes(s.id))]
}

export function GroupSheet({
  doc,
  group,
  services,
  problems,
  readOnly,
  onApply,
  onRemove,
  onOpenSegment,
  onAddSegment,
  onClose,
  trail,
}: {
  doc: OperationDoc
  group: ServiceGroup
  services: Service[]
  problems: ProfileProblem[]
  readOnly: boolean
  onApply: (g: ServiceGroup) => void
  onRemove: () => void
  /** Both carry the sheet's unapplied services and default, which the segment offers. */
  onOpenSegment: (segmentId: string, pending: GroupPending) => void
  onAddSegment: (pending: GroupPending) => void
  onClose: () => void
  /** The path back: the profiles list, the profile, and the group of a segment opened from it. */
  trail: Crumb[]
}) {
  const { lang, say } = useStaffText()
  const [name, setName] = useState(group.name)
  const [serviceIds, setServiceIds] = useState(group.serviceIds)
  const [defaultQueueId, setDefaultQueueId] = useState(group.defaultQueueId)
  // Only the order is held here: segments are edited in their own sheet, on the
  // page's document, so the group read from props always has their latest content.
  const [order, setOrder] = useState(() => group.segments.map((s) => s.id))

  const segments = ordered(group.segments, order)
  const next: ServiceGroup = { ...group, name, serviceIds, defaultQueueId, segments }
  const trial = useMemo(() => putGroup(doc, next), [doc, group, name, serviceIds, defaultQueueId, order])
  const live = useLiveProblems(trial)
  const mine = cardProblems(live, 'group', group.id, problems)
  const slots = useProblemSlots(mine, ['name', 'serviceIds', 'defaultQueueId', 'segments'])
  const segmentFlagged = (id: string) => live.some((p) => p.card.kind === 'segment' && p.card.id === id && !p.warning)

  const assignable = new Set(assignableServiceIds(doc, group.id, services.map((s) => s.id)))
  const listed = services.filter((s) => assignable.has(s.id) && (s.active || serviceIds.includes(s.id)))
  const strays = serviceIds.filter((id) => !services.some((s) => s.id === id))
  const toggle = (id: string, on: boolean) =>
    setServiceIds((ids) => (on ? [...ids.filter((x) => x !== id), id] : ids.filter((x) => x !== id)))

  const queues = queueOptions(
    doc.queues.filter((q) => q.type === 'normal' || q.id === defaultQueueId),
    lang,
  )
  const full = group.segments.length >= LIMITS.segmentsPerGroup

  const move = (id: string, by: number) => {
    const ids = segments.map((s) => s.id)
    const i = ids.indexOf(id)
    setOrder(moveItem(ids, i, i + by))
  }

  return (
    <InnerPage
      before={trail}
      title={name.trim() || say('profiles.group')}
      onClose={onClose}
      onSubmit={() => onApply({ ...next, name: name.trim() })}
      submitLabel={say('profiles.sheet.done')}
      hideSubmit={readOnly}
      asForm={false}
      extra={
        !readOnly && (
          <Confirm
            ask={say('profiles.gs.remove')}
            question={say('profiles.gs.removeAsk')}
            yes={say('profiles.sheet.remove')}
            keep={say('profiles.sheet.keep')}
            onYes={onRemove}
          />
        )
      }
    >
      {slots.head}
      <fieldset className="pf-sheet" disabled={readOnly}>
        <label className="field">
          <span>{say('profiles.sheet.name')}</span>
          <Input fullWidth value={name} aria-invalid={slots.has('name') || undefined} onChange={(e) => setName(e.target.value)} />
        </label>
        {slots.at('name')}

        <fieldset className="field">
          <legend>{say('profiles.sheet.services')}</legend>
          {listed.length === 0 && strays.length === 0 ? (
            <small>{say('profiles.gs.noServices')}</small>
          ) : (
            <div className="setup-ticks">
              {listed.map((s) => (
                <Tick key={s.id} checked={serviceIds.includes(s.id)} disabled={readOnly} onChange={(on) => toggle(s.id, on)}>
                  {nameIn(s.name, lang, s.code)}
                </Tick>
              ))}
              {strays.map((id) => (
                <Tick key={id} checked disabled={readOnly} onChange={() => toggle(id, false)}>
                  <span className="pf-gone">{say('profiles.sheet.serviceGone')}</span>
                </Tick>
              ))}
            </div>
          )}
        </fieldset>
        {slots.at('serviceIds')}

        <label className="field">
          <span>{say('profiles.gs.defaultQueue')}</span>
          <Pick label={say('profiles.gs.defaultQueue')} value={defaultQueueId} onChange={setDefaultQueueId} options={queues} disabled={readOnly} />
        </label>
        {slots.at('defaultQueueId')}
      </fieldset>

      <section className="field">
        <span className="pf-sheet__label">{say('profiles.gs.segments')}</span>
        {segments.length === 0 ? (
          <small className="pf-muted">{say('profiles.gs.noSegments')}</small>
        ) : (
          <ol className="pf-seglist">
            {segments.map((s, i) => (
              <li key={s.id} className="pf-seglist__row">
                <span className="pf-seglist__n">{i + 1}</span>
                <button type="button" className="pf-seglist__open" onClick={() => onOpenSegment(s.id, { serviceIds, defaultQueueId })}>
                  <span>{s.name.trim() || say('profiles.segment')}</span>
                  {segmentFlagged(s.id) && <Icon name="alert" size={16} />}
                </button>
                <MoveButtons
                  name={s.name}
                  first={i === 0}
                  last={i === segments.length - 1}
                  disabled={readOnly}
                  onMove={(by) => move(s.id, by)}
                />
              </li>
            ))}
          </ol>
        )}
        {!readOnly && (
          <span className="pf-sheet__add" title={full ? say('profiles.problem.TOO_MANY', { max: LIMITS.segmentsPerGroup }) : undefined}>
            <Button variant="ghost" size="sm" isDisabled={full} onPress={() => onAddSegment({ serviceIds, defaultQueueId })}>
              <Icon name="plus" size={16} />
              {say('profiles.gs.addSegment')}
            </Button>
          </span>
        )}
      </section>
      {slots.at('segments')}
    </InnerPage>
  )
}
